import {useCallback, useEffect, useRef, useState} from 'react';

import {downloadSpMetadata} from 'src/openapi/admin/apis/SsoApi';
import {useAppDispatch} from 'src/store/hooks';

type SpMetadataData = {
    entityId: string,
    acsUrl: string,
    xmlFile: string,
};

type UseDownloadSpMetadataReturn = {
    fetchMetadata: () => Promise<void>,
    saveFile: (xmlFile: string) => void,
    loading: boolean,
    error: string | null,
    data: SpMetadataData | null,
};

export function useDownloadSpMetadata(domains: string[]): UseDownloadSpMetadataReturn {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<SpMetadataData | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    const fetchMetadata = useCallback(async () => {
        if (domains.length === 0) {
            setError('No domains configured. Please go back and verify your domain.');
            return;
        }

        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setLoading(true);
        setError(null);

        try {
            const response = await dispatch(
                downloadSpMetadata(
                    {downloadSPMetadataRequest: {domains}},
                    {signal: controller.signal},
                ),
            );

            if (controller.signal.aborted) {
                return;
            }

            if (response.payload?.success) {
                const {entityId, acsUrl, xmlFile} = response.payload.data;
                if (!entityId || !acsUrl || !xmlFile) {
                    setData(null);
                    setError('Received incomplete SP metadata from the server. Please try again.');
                    return;
                }
                setData({
                    entityId,
                    acsUrl,
                    xmlFile,
                });
            } else {
                setError('Failed to download SP metadata. Please try again.');
            }
        } catch (err) {
            if ((err as {name?: string})?.name === 'AbortError') {
                return;
            }
            setError('Failed to download SP metadata. Please check your connection and try again.');
        } finally {
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }
            setLoading(false);
        }
    }, [dispatch, domains]);

    const saveFile = useCallback((xmlFile: string) => {
        if (xmlFile) {
            triggerFileDownload(xmlFile, 'pitchbook-sp-metadata.xml');
        }
    }, []);

    return {fetchMetadata, saveFile, loading, error, data};
}

function triggerFileDownload(xmlContent: string, filename: string): void {
    const blob = new Blob([xmlContent], {type: 'application/xml'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
